import * as React from 'react'
import { useState, useEffect } from 'react'
import RemoveFieldsButton from '../shared/RemoveFieldsButton'
import ItemPicker from './ItemPicker'
import RemovedField from './RemovedField'
import { WarehouseType } from '../CommonContext'
import { ItemShape } from '../../api/api_data_types'
import { stockLevelInWarehouse } from '../helpers/items'
import PlusMinusToggle from './PlusMinusToggle'

type ItemFieldsProps = {
  idKey: string
  itemFields: {
    id: number
    item: ItemShape
    amount: number
    warehouse_id: number
    exported: boolean
    _destroy: boolean
  }
  className?: string
  setItemFields: (itemFields: any) => void
  fieldNameFn: (field: string) => string
  handleRemove: () => void
  availableWarehouses?: WarehouseType[]
  loadItemOptions?: (filter: any, callback: any) => any
  showSignControl?: boolean
  // loadItemOptions?: (filter: any, callback: any) => Promise<any[]>
}

export default function ItemFields(props: ItemFieldsProps) {
  const {
    idKey,
    className,
    itemFields,
    setItemFields,
    fieldNameFn,
    handleRemove,
    availableWarehouses,
    loadItemOptions = null,
    showSignControl = false,
  } = props

  const amount = itemFields.amount || 0
  const exported = itemFields.exported || false
  // Sign lives in state (not derived from the amount) so it survives an empty/zero amount
  const [signMultiplier, setSignMultiplier] = useState(parseFloat(amount.toString().replace(',', '.')) < 0 ? -1 : 1)
  const fieldId = field => `${idKey}-field-${field}`

  const id = itemFields.id || null
  const defaultItem = itemFields.item
    ? {
        label: itemFields.item.title,
        value: itemFields.item.id,
        item: itemFields.item,
      }
    : null
  const [item, setItem] = useState(defaultItem)
  useEffect(() => {
    if (item) {
      setItemFields({ ...itemFields, item_id: item.value, item: item.item })
    }
  }, [item])
  const defaultWarehouseId = itemFields.warehouse_id || (availableWarehouses ? availableWarehouses[0]['id'] : null)
  const [warehouseId, setWarehouseId] = useState(defaultWarehouseId)
  useEffect(() => {
    if (availableWarehouses && warehouseId) {
      setItemFields({ ...itemFields, warehouse_id: warehouseId })
    }
  }, [warehouseId])
  const baseUnit = (item && item.item && (item.item.default_consumption || item.item.base_unit_of_measure)) || null
  // const stockLevel = item && item.item ? item.item.inventory : null
  const stockLevel = stockLevelInWarehouse(item, warehouseId, availableWarehouses)
  const isOverStock = stockLevel === null ? false : stockLevel < amount

  if (itemFields._destroy === true) {
    return <RemovedField field={itemFields} fieldNameFn={fieldNameFn} />
  }

  return (
    <fieldset className={`resources-time-fields form-fieldset ${className}`}>
      {!exported && <RemoveFieldsButton className="resources-remove-fields-button" handleRemove={handleRemove} />}
      <div className="row">
        <div className="col-12 form-group">
          <label htmlFor={fieldId('item_id')}>Vareforbrug</label>
          <ItemPicker
            isDisabled={exported}
            className="resources-item-picker"
            customLoadOptions={loadItemOptions}
            setItem={setItem}
            item={item}
            id={fieldId('item_id')}
            name={fieldNameFn('item_id')}
          />
        </div>
      </div>
      <div className="row">
        {availableWarehouses && (
          <div className="col-sm-8 form-group">
            <label htmlFor={fieldId('warehouse')}>Lager</label>
            <select
              id={fieldId('warehouse')}
              required
              className="form-control col-xs-12"
              name={fieldNameFn('warehouse_id')}
              value={warehouseId}
              disabled={exported}
              onChange={e => setWarehouseId(parseInt(e.target.value, 10))}
            >
              {availableWarehouses.map(wh => {
                return (
                  <option key={wh.id} value={wh.id}>
                    {wh.location_code_title}
                  </option>
                )
              })}
            </select>
          </div>
        )}
        <div className={availableWarehouses ? 'col-sm-4' : 'col-sm-12'}>
          <label htmlFor={fieldId('amount')}>Antal</label>
          <div className="d-flex">
            {showSignControl && (
              <PlusMinusToggle
                disabled={exported}
                signMultiplier={signMultiplier}
                onSignChange={newSignMultiplier => {
                  setSignMultiplier(newSignMultiplier)
                  const magnitude = amount.toString().replace(/^-/, '')
                  setItemFields({ ...itemFields, amount: newSignMultiplier < 0 ? `-${magnitude}` : magnitude })
                }}
              />
            )}
            <div className="input-group">
              <input
                id={fieldId('amount')}
                className={`form-control ${isOverStock ? 'resources-item-over-stock' : ''}`}
                type="text"
                step="0.1"
                pattern="[0-9]+([.,][0-9]{1,2})?"
                value={amount.toString().replace(/^-/, '')}
                disabled={exported}
                onFocus={e => e.target.select()}
                onChange={e => {
                  const val = e.target.value
                  let newValue = val.replace(/^-/, '')
                  if (signMultiplier < 0) {
                    newValue = `-${newValue}`
                  }

                  setItemFields({ ...itemFields, amount: newValue })
                }}
              />
              <div className="input-group-append">
                <span className="input-group-text">{baseUnit || '.'}</span>
              </div>
            </div>
          </div>
        </div>
        {/* The visible input holds the unsigned magnitude; the signed value is submitted here */}
        <input type="hidden" disabled={exported} name={fieldNameFn('amount')} value={amount} />
        {id && <input type="hidden" disabled={exported} name={fieldNameFn('id')} value={id} />}
      </div>
      {isOverStock && <div className="resources-item-over-stock-notice">* Antal overstiger antal på lager ({stockLevel})</div>}
    </fieldset>
  )
}
